import { getLayoverLogger } from '@/logging/LayoverLogger';

interface MimeTypeAndExtension {
  mimeType: string;
  fileExtension: string;
}

export type ExportFormat = 'json' | 'markdown' | 'bibtex';

const formatTypeToMimeType: Record<ExportFormat, MimeTypeAndExtension> = {
  json: { mimeType: 'application/json', fileExtension: 'json' },
  markdown: { mimeType: 'text/markdown', fileExtension: 'md' },
  bibtex: { mimeType: 'application/x-bibtex', fileExtension: 'bib' },
};

const downloadFile = (content: string, filename: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();

  URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

const arrayStrToBibtex = (jsonString: string): string => {
  try {
    const data = JSON.parse(jsonString);

    if (!Array.isArray(data)) {
      throw new Error('Invalid JSON format: Expected an array of objects.');
    }

    return data
      .map((entry, index) => {
        if (!entry.paper) {
          console.warn(`Skipping entry at index ${index}: Missing paper data.`);
          return 'Skipping entry: Missing paper data.';
        }

        const { id, paper } = entry;
        const { title, year, venue, authors } = paper;

        if (!id || !title || !year || !authors) {
          console.warn(
            `Skipping entry at index ${index}: Missing required fields (id, title, year, authors).`,
          );
          return `Skipping entry at index ${index}: Missing required fields.`;
        }

        const formattedAuthors = authors
          .map((a: { name: string }) => a.name)
          .join(' and ');
        const sanitizedId = id.replace(/\W/g, '');
        return `@article{${sanitizedId},
  author = {${formattedAuthors}},
  title = {${title}},
  year = {${year}},
  ${venue ? `journal = {${venue}},` : ''}
}`;
      })
      .filter(Boolean)
      .join('\n\n');
  } catch (error: any) {
    getLayoverLogger().logError(
      'Error converting JSON array to BibTeX:',
      error,
    );
    return 'Error parsing the JSON string.';
  }
};

export function downloadCitations(
  content: string,
  formatType: ExportFormat,
): void {
  const formatContent = arrayStrToBibtex(content);
  const { mimeType, fileExtension } = formatTypeToMimeType[formatType];
  const fileName = `citations.${fileExtension}`;
  downloadFile(formatContent, fileName, mimeType);
}
